import React, { useState, useEffect, useRef } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import {
    Box,
    Button,
    Drawer,
    TextField,
    Typography,
    IconButton,
    MenuItem,
    Snackbar,
    Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { Upload } from "lucide-react";
import Table from "./CommonUserTable";
import {
    apiCallCreatePublicUser,
    apiCallActiveProfileTypeList,
    apiCallUserUpdate
} from "../../../../Core/Apicall";
import { UploadImages } from "../../../../Core/UploadImages";
import { countryCodeList } from "../../../../Core/CountryCodes";

function PublicUser() {
    const [openDrawer, setOpenDrawer] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [refreshKey, setRefreshKey] = useState(0);

    const profileInputRef = useRef<HTMLInputElement | null>(null);
    const coverInputRef = useRef<HTMLInputElement | null>(null);

    const [profileUploadProgress, setProfileUploadProgress] = useState(0);
    const [coverUploadProgress, setCoverUploadProgress] = useState(0);

    const [profileTypes, setProfileTypes] = useState<any[]>([]);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success" as "success" | "error",
    });

    const initialForm = {
        _id: "",
        firstName: "",
        lastName: "",
        fullName: "",
        emailId: "",
        password: "",
        phoneCountryCode: "",
        phoneNumber: "",
        gender: "",
        profileTypeId: "",
        bio: "",
        profileImagePath: "",
        profileCoverImagePath: "",
        dateOfBirth: "",
    };

    const [formData, setFormData] = useState(initialForm);

    // ------------------ Fetch profile types ------------------
    useEffect(() => {
        apiCallActiveProfileTypeList(undefined).then((res) => {
            setProfileTypes(res?.data || []);
        });
    }, []);

    // ------------------ Handlers ------------------
    const handleChange = (e: any) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleProfileImageUpload = async (file: File) => {
        const res = await UploadImages(
            file,
            "profile",
            null,
            null,
            null,
            (p: number) => setProfileUploadProgress(p)
        );

        if (res?.thumbnail || res?.location) {
            setFormData((p) => ({
                ...p,
                profileImagePath: res.thumbnail || res.location,
            }));
            setProfileUploadProgress(100);
        }
    };

    const handleCoverImageUpload = async (file: File) => {
        const res = await UploadImages(file, "profile", null, null, null, (p: number) =>
            setCoverUploadProgress(p)
        );
        if (res?.thumbnail || res?.location) {
            setFormData((p) => ({
                ...p,
                profileCoverImagePath: res.thumbnail || res.location,
            }));
        }
    };

    // ------------------ CREATE ------------------
    const createPublicUser = async () => {
        try {
            await apiCallCreatePublicUser(formData);
            success("Public user created successfully");
        } catch (err: any) {
            error(err);
        }
    };

    const formatDateToYYYYMMDD = (date: string | Date) => {
        if (!date) return "";
        const d = new Date(date);
        return d.toISOString().split("T")[0];
    };

    // ------------------ UPDATE ------------------
    const updatePublicUser = async () => {
        try {
            const paylod = {
                userId: formData._id,
                ...formData
            }
            await apiCallUserUpdate(paylod);
            success("Public user updated successfully");
        } catch (err: any) {
            error(err);
        }
    };

    const success = (msg: string) => {
        setSnackbar({ open: true, message: msg, severity: "success" });
        closeDrawer();
        setRefreshKey((p) => p + 1);
    };

    const error = (err: any) => {
        setSnackbar({
            open: true,
            message: err?.response?.data?.message || "Something went wrong",
            severity: "error",
        });
    };

    const closeDrawer = () => {
        setOpenDrawer(false);
        setIsEdit(false);
        setSelectedUser(null);
        setFormData(initialForm);
    };

    return (
        <Box>
            {/* Header */}
            {/* <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{ backgroundColor: "#000", borderRadius: "10px" }}
                    onClick={() => setOpenDrawer(true)}
                >
                    Add Public User
                </Button>
            </Box> */}

            {/* TABLE */}
            <Table
                role={6}
                key={refreshKey}
                onEdit={(user: any) => {
                    setIsEdit(true);
                    setFormData({
                        ...initialForm,
                        ...user,
                        password: "",
                    });
                    setOpenDrawer(true);
                }}
                onCreate={() => {
                    setIsEdit(false);
                    setFormData(initialForm);
                    setOpenDrawer(true);
                }}
            />

            {/* DRAWER */}
            <Drawer anchor="right" open={openDrawer} onClose={closeDrawer} PaperProps={{ sx: { width: "35%" } }}>
                <Box p={3}>
                    <Box display="flex" justifyContent="space-between" mb={2}>
                        <Typography variant="h6">
                            {isEdit ? "Edit Public User" : "Add Public User"}
                        </Typography>
                        <IconButton onClick={closeDrawer}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <TextField
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        label="Email"
                        name="emailId"
                        value={formData.emailId}
                        onChange={handleChange}
                        fullWidth
                        disabled={isEdit}
                        helperText={isEdit ? "Email cannot be changed" : ""}
                        sx={{ mb: 2 }}
                    />

                    {!isEdit && (
                        <TextField
                            label="Password"
                            name="password"
                            type="password"
                            value={formData.password}
                            onChange={handleChange}
                            fullWidth
                            sx={{ mb: 2 }}
                        />
                    )}


                    <TextField
                        select
                        label="Country Code"
                        name="phoneCountryCode"
                        value={formData.phoneCountryCode}
                        onChange={handleChange}
                        disabled={isEdit}
                        fullWidth
                        sx={{ mb: 2 }}
                    >
                        {countryCodeList.map((c) => (
                            <MenuItem key={c.code} value={c.code}>
                                {c.country} ({c.code})
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Phone"
                        name="phoneNumber"
                        value={formData.phoneNumber}
                        disabled={isEdit}
                        onChange={handleChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        type="date"
                        label="DOB"
                        name="dateOfBirth"
                        value={formatDateToYYYYMMDD(formData.dateOfBirth)}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        sx={{ mb: 2 }}
                    />


                    <TextField select label="Gender" name="gender" value={formData.gender} onChange={handleChange} fullWidth sx={{ mb: 2 }}>
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                    </TextField>

                    <TextField
                        select
                        label="Profile Type"
                        name="profileTypeId"
                        value={formData.profileTypeId}
                        onChange={handleChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    >
                        {profileTypes.map((p) => (
                            <MenuItem key={p._id} value={p._id}>
                                {p.typeName}
                            </MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        label="Bio"
                        name="bio"
                        multiline
                        rows={3}
                        value={formData.bio}
                        onChange={handleChange}
                        fullWidth
                        sx={{ mb: 2 }}
                    />

                    {/* Uploads */}
                    <input hidden ref={profileInputRef} type="file" onChange={(e) => e.target.files && handleProfileImageUpload(e.target.files[0])} />
                    <UploadBox label="Profile Image" image={formData.profileImagePath} progress={profileUploadProgress} onClick={() => profileInputRef.current?.click()} />

                    <input hidden ref={coverInputRef} type="file" onChange={(e) => e.target.files && handleCoverImageUpload(e.target.files[0])} />
                    <UploadBox label="Cover Image" image={formData.profileCoverImagePath} progress={coverUploadProgress} onClick={() => coverInputRef.current?.click()} />

                    <Button fullWidth variant="contained" sx={{ mt: 2, background: "#000" }} onClick={isEdit ? updatePublicUser : createPublicUser}>
                        {isEdit ? "Update User" : "Save User"}
                    </Button>
                </Box>
            </Drawer>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
}

/* Upload Box */
const UploadBox = ({
    label,
    image,
    progress,
    onClick,
}: {
    label: string;
    image?: string;
    progress: number;
    onClick: () => void;
}) => {
    const isUploading = progress > 0 && progress < 100;

    return (
        <Box
            onClick={!isUploading ? onClick : undefined}
            sx={{
                border: "1px dashed #ccc",
                p: 2,
                mb: 2,
                borderRadius: 2,
                cursor: isUploading ? "not-allowed" : "pointer",
                textAlign: "center",
                opacity: isUploading ? 0.6 : 1,
            }}
        >
            {/* IMAGE PREVIEW */}
            {image && !isUploading && (
                <img src={image} alt="" height={100} style={{ marginBottom: 8 }} />
            )}

            {/* UPLOADING STATE */}
            {isUploading ? (
                <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <CircularProgress size={28} />
                    <Typography variant="body2">
                        {label} is uploading… {progress}%
                    </Typography>
                </Box>
            ) : (
                <>
                    {!image && (
                        <>
                            <Upload size={20} />
                            <Typography variant="body2">{label}</Typography>
                        </>
                    )}
                </>
            )}
        </Box>
    );
};


export default PublicUser;
